import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import "./chat-mic.component.scoped.scss";
import { IconButton } from "@material-ui/core";
import MicNoneOutlinedIcon from "@material-ui/icons/MicNoneOutlined";
import StopIcon from "@material-ui/icons/Stop";

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

interface ChatMicProps {
  onTranscript: (transcript: string) => void;
  disabled?: boolean;
  language?: 'en-US' | 'de-DE';
}

const ChatMic = forwardRef<any, ChatMicProps>(({ onTranscript, disabled = false, language = 'en-US' }, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Expose stopRecording to parent via ref
  useImperativeHandle(ref, () => ({
    stopRecording: () => {
      if (recognitionRef.current && isRecording) {
        recognitionRef.current.stop();
        setIsRecording(false);
      }
    },
    isRecording: () => isRecording,
  }), [isRecording]);

  // Initialize speech recognition
  const initializeSpeechRecognition = () => {
    if (!recognitionRef.current) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        if (recognitionRef.current) {
          recognitionRef.current.continuous = true;
          recognitionRef.current.interimResults = true;
          recognitionRef.current.lang = language;

          recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
              const transcript = event.results[i][0].transcript;
              if (event.results[i].isFinal) {
                finalTranscript += transcript;
              }
            }

            if (finalTranscript) {
              onTranscript(finalTranscript);
            }
          };

          recognitionRef.current.onerror = (event: SpeechRecognitionErrorEvent) => {
            console.error('Speech recognition error:', event.error);
            setIsRecording(false);
          };

          recognitionRef.current.onend = () => {
            setIsRecording(false);
          };
        }
      }
    }
  };

  const handleMicrophoneClick = () => {
    if (!recognitionRef.current) {
      initializeSpeechRecognition();
    }

    if (recognitionRef.current) {
      if (isRecording) {
        recognitionRef.current.stop();
        setIsRecording(false);
      } else {
        recognitionRef.current.start();
        setIsRecording(true);
      }
    } else {
      console.error('Speech recognition not supported');
      alert('Speech recognition is not supported in this browser');
    }
  };

  return (
    <IconButton
      className={`chat-mic-button ${isRecording ? 'recording' : ''}`}
      onClick={handleMicrophoneClick}
      style={{ color: '#1D1A21' }}
      disabled={disabled}
      size="small"
      title={isRecording ? "Stop recording" : "Start voice recording"}
    >
      {isRecording ? (
        <StopIcon style={{ color: '#fff' }} />
      ) : (
        <MicNoneOutlinedIcon style={{ color: '#1D1A21' }} />
      )}
    </IconButton>
  );
});

ChatMic.displayName = "ChatMic";

export default ChatMic; 